import { useState } from "react";
import { ChevronDown, ChevronRight, Trash2, Database, Calculator } from "lucide-react";

const IncineratorFuelConsumption = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const calculations = [
    {
      id: "daily-incinerator-breakdown",
      title: "Incinerator Fuel Breakdown",
      description: "Calculates incinerator fuel consumption for each fuel type per day, handling missing data",
      formula: "Daily = {HFO: incineratorConsHfo, LFO: incineratorConsLfo, MGO: incineratorConsMgo, Others: incineratorConsOthers}",
      subFormulas: [
        "HFO = record?.incineratorConsHfo || 0",
        "LFO = record?.incineratorConsLfo || 0",
        "MGO = record?.incineratorConsMgo || 0",
        "Others = record?.incineratorConsOthers || 0",
        "Daily Total = HFO + LFO + MGO + Others"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "incinerator-period-totals",
      title: "Incinerator Total Calculations",
      description: "Sums incinerator consumption for each fuel type across the entire period",
      formula: "Incinerator_Total_FuelType = Σ(Daily incinerator consumption for that fuel type)",
      subFormulas: [
        "Total Incinerator HFO = Σ(incineratorConsHfo for each day)",
        "Total Incinerator LFO = Σ(incineratorConsLfo for each day)",
        "Total Incinerator MGO = Σ(incineratorConsMgo for each day)",
        "Total Incinerator Others = Σ(incineratorConsOthers for each day)",
        "Overall Incinerator Total = Sum of all fuel types"
      ],
      icon: <Database className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
  ];

  const dataStructure = {
    collection: "VesselEmissionRawData",
    incineratorSpecificFields: [
      { name: "incineratorConsHfo" },
      { name: "incineratorConsLfo" },
      { name: "incineratorConsMgo" },
      { name: "incineratorConsOthers" }
    ],
  };

  const responseStructure = {
    flags: [
      { name: "hasData", value: "true", description: "Always true to ensure timeline consistency" },
      { name: "hasActualConsumptionData", value: "true/false", description: "Indicates if real incinerator data exists for the period" }
    ],
    dataFormat: {
      dailyData: [
        "date: YYYY-MM-DD",
        "fullDate: YYYY-MM-DD",
        "HFO: incineratorConsHfo or 0",
        "LFO: incineratorConsLfo or 0", 
        "MGO: incineratorConsMgo or 0",
        "others: incineratorConsOthers or 0",
        "total: sum of all fuel types"
      ],
      totals: [
        "HFO: sum of daily HFO values",
        "LFO: sum of daily LFO values",
        "MGO: sum of daily MGO values", 
        "others: sum of daily others values",
        "total: sum of all daily totals"
      ]
    }
  };

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#b6faed] rounded-lg">
              <Trash2 className="w-6 h-6 text-[#117864]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Incinerator Fuel Consumption Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Waste incineration fuel usage analysis and tracking methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-6">
                <h4 className="font-semibold text-gray-700 mb-3">Incinerator Data Source</h4>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="mb-4">
                    <div className="font-medium text-gray-800 mb-2">Collection: {dataStructure.collection}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      {dataStructure.incineratorSpecificFields.map((field, index) => (
                        <div key={index} className="bg-white p-3 rounded border">
                          <div className="font-medium text-gray-800 text-sm">{field.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Detailed Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {calculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Primary Logic:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncineratorFuelConsumption;